import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin } from "lucide-react";

/**
 * City autocomplete that calls /api/cities?q= as the user types.
 * Calls onSelect(cityObj) when a suggestion is picked so the parent can auto-fill
 * country + latitude + longitude in addition to city.
 */
export default function CityAutocomplete({
  value, onChange, onSelect, country, placeholder = "Start typing a city…", testId = "city-autocomplete",
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!value || value.length < 1) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { q: value, limit: 8 };
        if (country && country !== "all") params.country = country;
        const { data } = await api.get("/cities", { params });
        setResults(data);
        setActive(0);
      } catch {
        setResults([]);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, country]);

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (c) => {
    onChange(c.name);
    onSelect && onSelect(c);
    setOpen(false);
    setResults([]);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef} data-testid={testId}>
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => value && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11"
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 border border-border bg-[#F9F6F0] rounded-sm shadow-sm max-h-72 overflow-auto"
          data-testid={`${testId}-suggestions`}
        >
          {results.map((c, i) => (
            <button
              type="button"
              key={c.slug || `${c.name}-${i}`}
              onClick={() => pick(c)}
              onMouseEnter={() => setActive(i)}
              data-testid={`${testId}-option-${i}`}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${i === active ? "bg-[#F2EBE1]" : ""}`}
            >
              <div>
                <p className="text-sm text-[#2C1E16]">{c.name}</p>
                <p className="text-xs text-[#786B63] inline-flex items-center gap-1">
                  <MapPin size={10} strokeWidth={1.5} />
                  {c.region ? `${c.region}, ` : ""}{c.country}
                </p>
              </div>
              {c.count > 0 && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-border rounded-full text-[#786B63]">
                  {c.count} spot{c.count === 1 ? "" : "s"}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
